import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import { useConnectDataAttributes } from '@/libs/api/connect/connect.composables.ts'
import type { SettingsLinexo, Translations } from '@/libs/api/connect/connect.types.ts'
import { deepMerge } from '@/utils/data-utils.ts'
import { language, setI18nLanguage } from '@/i18n/i18n.ts'
import { type Composer } from 'vue-i18n'
import { DEFAULT_LOCALE } from '@/ts/constants/constants.ts'

export type ConnectServiceHooks = {
  onInitLinexoSettings?: (settings: SettingsLinexo) => Promise<void> | void
  onInitError?: (error: unknown) => void
  onPostInit?: () => Promise<void>
}

export const useConnectService = (
  connectStore: ReturnType<typeof useConnectStore>,
  i18nInstance: Composer,
  hooks?: ConnectServiceHooks
) => {
  const { mergeLocaleMessage, getLocaleMessage, setLocaleMessage } = i18nInstance

  async function init(): Promise<void> {
    try {
      await connectStore.loadPartnerConfig()

      if (connectStore.getLinexoSettings) {
        hooks?.onInitLinexoSettings?.(connectStore.getLinexoSettings)
      }

      mergeTranslations(
        connectStore.getTranslations!,
        connectStore.getOverrideTranslations!,
        useLanguageKeys()
      )

      if (hooks?.onPostInit) await hooks?.onPostInit()
    } catch (error) {
      hooks?.onInitError?.(error)
    }
  }

  function useLanguageKeys() {
    const { locale } = useConnectDataAttributes()

    const defaultLanguageKey = language(DEFAULT_LOCALE)
    const currentLanguageKey = language(locale!)
    return { defaultLanguageKey, currentLanguageKey }
  }

  function mergeTranslations(
    bootstrapTranslations: Record<string, Translations>,
    overrideTranslations: Record<string, Translations>,
    languageKeys: { defaultLanguageKey: string; currentLanguageKey: string }
  ): Record<string, Translations> {
    const { defaultLanguageKey, currentLanguageKey } = languageKeys

    const defaultMessages = getLocaleMessage(defaultLanguageKey)
    const messages = getLocaleMessage(currentLanguageKey)

    const mergedTranslations = deepMerge(
      defaultMessages,
      messages,
      bootstrapTranslations[currentLanguageKey],
      overrideTranslations[currentLanguageKey]
    )

    i18nInstance.setLocaleMessage(currentLanguageKey, messages)
    setI18nLanguage(i18nInstance, currentLanguageKey)

    return mergedTranslations
  }

  return {
    init,
    mergeTranslations,
    useLanguageKeys
  }
}
